import React, { useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useTranslation } from "react-i18next";
import { getUrlApi } from "../../utilities/util";
import styles from "./index.module.css";

export interface VagaEventImageProps {
  /** Código/identificador da vaga (= parking_space.Code, ex.: "vaga_02"). */
  vagaKey: string;
  /** Muda quando há novo evento (usado para cache-bust da imagem). */
  version?: string | null;
}

/** Mapeia o idioma do i18n para o segmento de cultura da API (mesma regra do ApiHandler). */
const cultureSegment = (lang?: string): string =>
  (lang || "").toLowerCase().startsWith("en") ? "en-us" : "pt-br";

/**
 * Miniatura da imagem do último evento da vaga; ao clicar, expande em um modal.
 * A imagem vem de GET {culture}/api/ParkingImage/vaga/{vagaKey}/last-image (404 → sem imagem).
 */
const VagaEventImage: React.FC<VagaEventImageProps> = ({ vagaKey, version }) => {
  const { t, i18n } = useTranslation("occupancyMap");
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);

  const src = useMemo(() => {
    const base = (getUrlApi() || "").toString().replace(/\/+$/, "");
    const culture = cultureSegment(i18n.language);
    const bust = version ? `?v=${encodeURIComponent(version)}` : "";
    return `${base}/${culture}/api/ParkingImage/vaga/${encodeURIComponent(vagaKey)}/last-image${bust}`;
  }, [vagaKey, version, i18n.language]);

  if (errored) {
    return (
      <div className={styles.eventImageEmpty}>
        <span className="material-symbols-outlined" aria-hidden="true">
          no_photography
        </span>
        {t("noEventImage")}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={styles.eventThumbBtn}
        onClick={() => setOpen(true)}
        aria-label={t("lastEventImage")}
      >
        <img
          src={src}
          alt={t("lastEventImage")}
          className={styles.eventThumb}
          onError={() => setErrored(true)}
        />
        <span className={`material-symbols-outlined ${styles.eventThumbZoom}`} aria-hidden="true">
          zoom_in
        </span>
      </button>

      <Dialog
        header={t("lastEventImage")}
        visible={open}
        onHide={() => setOpen(false)}
        dismissableMask
        // Acima do bottom sheet da vaga (z 1210): assim o modal da imagem fica na frente
        // e o clique fora/no X fecha só a imagem — o sheet só fecha num 2º clique fora dele.
        baseZIndex={2000}
        style={{ width: "min(90vw, 900px)" }}
      >
        <img
          src={src}
          alt={t("lastEventImage")}
          style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
        />
      </Dialog>
    </>
  );
};

export default VagaEventImage;
